import axios from 'axios';
import { AlertTriangle, LoaderCircle, RefreshCw } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { CreateTicketFormCard } from './components/CreateTicketFormCard';
import { ReplyTicketComposer } from './components/ReplyTicketComposer';
import { SlaCard } from './components/SlaCard';
import { TicketListContent } from './components/TicketListContent';
import { TicketMessageList } from './components/TicketMessageList';
import {
  buildTicketsWorkspaceText,
  extractErrorMessage,
  getBlockedMessage,
  getFormattedDate,
  getPriorityClassName,
  getPriorityLabel,
  getStatusClassName,
  getStatusFromError,
  getStatusLabel,
  getTicketCustomerName,
  getTicketMessageAuthorName,
  getTicketOpenerName,
  getTicketPriorityUpdatedByName,
  hasMessageOrAttachment,
  isTicketRole,
  readImageDimensions,
  TicketsWorkspaceText,
} from './utils';

import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/Select';
import { Role, useAuth } from '@/contexts/AuthContext';
import {
  useCreateTicket,
  useFinalizeTicket,
  useReopenTicket,
  useSendTicketMessage,
  useTicketDetail,
  useTicketsList,
  useUpdateTicketPriority,
} from '@/hooks/useTickets';
import { cn } from '@/lib/utils';
import TicketsService, {
  CreateTicketPayload,
  TicketAttachment,
  TicketDetail,
  TicketMessage,
  TicketAttachmentPayload,
  TicketPriority,
  TicketSummary,
  TicketStatus,
} from '@/services/TicketsService';

const PAGE_SIZE = 20;
const MAX_ATTACHMENTS_PER_MESSAGE = 3;
const MAX_IMAGE_SIZE_BYTES = 2 * 1024 * 1024;
const ALLOWED_MIME_TYPES = ['image/jpeg', 'image/png', 'image/webp'] as const;

type StatusFilter = TicketStatus | 'all';
type PriorityFilter = TicketPriority | 'all';
type TicketsWorkspaceVariant = 'default' | 'admin';

interface TicketsWorkspaceProps {
  variant?: TicketsWorkspaceVariant;
}

function handleErrorToast(
  error: unknown,
  fallback: string,
  formatMessage?: (value: string) => string,
) {
  const message = extractErrorMessage(error, fallback, formatMessage);
  toast.error(message);
}

export function TicketsWorkspace({
  variant = 'default',
}: TicketsWorkspaceProps) {
  const { t, i18n } = useTranslation('translation', {
    keyPrefix: 'pages.Tickets',
  });
  const { user } = useAuth();
  const isAdminVariant = variant === 'admin';

  const roleId = user?.role.id;
  const isRoot = roleId === Role.Root;
  const isAdmin = roleId === Role.Admin;
  const hasTicketAccess = isTicketRole(roleId);

  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [priorityFilter, setPriorityFilter] = useState<PriorityFilter>('all');
  const [subjectFilter, setSubjectFilter] = useState('');
  const [descriptionFilter, setDescriptionFilter] = useState('');
  const [customerIdFilter, setCustomerIdFilter] = useState('');
  const [offset, setOffset] = useState(0);
  const [selectedTicketId, setSelectedTicketId] = useState<number | null>(null);

  const [selectedTicketPriority, setSelectedTicketPriority] =
    useState<TicketPriority>('low');

  const [downloadingAttachmentId, setDownloadingAttachmentId] = useState<
    number | null
  >(null);

  const text: TicketsWorkspaceText = useMemo(
    () =>
      buildTicketsWorkspaceText({
        isAdminVariant,
        t: (key: string, options?: Record<string, unknown>) =>
          t(key as any, options as any) as string,
        maxAttachmentsPerMessage: MAX_ATTACHMENTS_PER_MESSAGE,
      }),
    [isAdminVariant, t],
  );

  const dateLocale = isAdminVariant ? 'en-US' : i18n.language;

  function translatePriorityEnumsForUserUi(message: string) {
    if (isAdminVariant) {
      return message;
    }

    return message.replace(/\b(low|high|critical)\b/gi, (match) => {
      const normalizedPriority = match.toLowerCase() as TicketPriority;
      return getPriorityLabel(normalizedPriority, text.priorityLabels);
    });
  }

  function handleTicketErrorToast(error: unknown, fallback: string) {
    handleErrorToast(error, fallback, translatePriorityEnumsForUserUi);
  }

  const normalizedSubjectFilter = subjectFilter.trim();
  const normalizedDescriptionFilter = descriptionFilter.trim();
  const parsedCustomerId = customerIdFilter.trim()
    ? Number(customerIdFilter.trim())
    : undefined;
  const customerIdFilterValue =
    isAdminVariant && isRoot && Number.isInteger(parsedCustomerId)
      ? parsedCustomerId
      : undefined;

  const listQuery = useMemo(
    () => ({
      status: statusFilter === 'all' ? undefined : statusFilter,
      priority: priorityFilter === 'all' ? undefined : priorityFilter,
      subject:
        isAdminVariant && normalizedSubjectFilter
          ? normalizedSubjectFilter
          : undefined,
      description:
        isAdminVariant && normalizedDescriptionFilter
          ? normalizedDescriptionFilter
          : undefined,
      customerId: customerIdFilterValue,
      limit: PAGE_SIZE,
      offset,
    }),
    [
      customerIdFilterValue,
      isAdminVariant,
      normalizedDescriptionFilter,
      normalizedSubjectFilter,
      offset,
      priorityFilter,
      statusFilter,
    ],
  );

  const {
    tickets,
    total,
    limit,
    isLoading: isTicketsLoading,
    error: ticketsError,
    refetch: refetchTickets,
  } = useTicketsList(listQuery, hasTicketAccess);

  const {
    ticket: selectedTicket,
    isLoading: isSelectedTicketLoading,
    error: selectedTicketError,
    refetch: refetchSelectedTicket,
  } = useTicketDetail(selectedTicketId, hasTicketAccess);

  const { mutateAsync: createTicket, isPending: isCreatingTicket } =
    useCreateTicket();
  const { mutateAsync: sendTicketMessage, isPending: isSendingTicketMessage } =
    useSendTicketMessage();
  const { mutateAsync: finalizeTicket, isPending: isFinalizingTicket } =
    useFinalizeTicket();
  const { mutateAsync: reopenTicket, isPending: isReopeningTicket } =
    useReopenTicket();
  const { mutateAsync: updateTicketPriority, isPending: isUpdatingPriority } =
    useUpdateTicketPriority();

  useEffect(() => {
    setOffset(0);
  }, [
    statusFilter,
    priorityFilter,
    normalizedSubjectFilter,
    normalizedDescriptionFilter,
    customerIdFilterValue,
  ]);

  useEffect(() => {
    if (tickets.length === 0) {
      setSelectedTicketId(null);
      return;
    }

    if (
      !selectedTicketId ||
      !tickets.some((ticket) => ticket.id === selectedTicketId)
    ) {
      setSelectedTicketId(tickets[0].id);
    }
  }, [tickets, selectedTicketId]);

  useEffect(() => {
    if (selectedTicket) {
      setSelectedTicketPriority(selectedTicket.priority ?? 'low');
    }
  }, [selectedTicket]);

  const totalPages = Math.max(1, Math.ceil(total / limit));
  const currentPage = Math.floor(offset / limit) + 1;

  const canCreateTicket = isAdmin;
  const isSelectedTicketFinalized = selectedTicket?.status === 'finalized';
  const isReplyBlocked =
    isSelectedTicketFinalized ||
    (isAdmin && Boolean(selectedTicket?.awaitingRootReply));

  const replyBlockedMessage = getBlockedMessage({
    isAdmin,
    awaitingRootReply: selectedTicket?.awaitingRootReply,
    isFinalized: Boolean(isSelectedTicketFinalized),
    finalizedMessage: text.blockedFinalized,
    awaitingMessage: text.blockedAwaiting,
  });

  function renderTicketOwnership(
    ticket: TicketSummary | TicketDetail,
    className?: string,
  ) {
    const ticketUserName = getTicketOpenerName(ticket) ?? '-';

    return (
      <p className={cn('text-xs text-gray-500', className)}>
        <span className="font-medium text-gray-600">
          User: {ticketUserName} ({ticket.createdByUserId})
        </span>
        {' · '}

        <span className="font-medium text-gray-600">
          Customer: {getTicketCustomerName(ticket) ?? '-'} ({ticket.customerId})
        </span>
      </p>
    );
  }

  function getMessageAuthorLabel(message: TicketMessage) {
    const authorName = getTicketMessageAuthorName(
      message,
      selectedTicket,
      text.userFallback,
      user
        ? {
            id: user.id,
            firstName: user.firstName ?? '',
            lastName: user.lastName ?? '',
          }
        : null,
    );

    if (message.authorRole === 'root') {
      return `${authorName} (${text.supportSuffix})`;
    }

    return authorName;
  }

  function clearListFilters() {
    setStatusFilter('all');
    setPriorityFilter('all');
    setSubjectFilter('');
    setDescriptionFilter('');
    setCustomerIdFilter('');
  }

  async function uploadAttachments(files: File[]) {
    const attachments = await Promise.all(
      files.map(async (file): Promise<TicketAttachmentPayload> => {
        const presign = await TicketsService.generateUploadPresign({
          fileName: file.name,
          contentType: file.type,
          fileSizeBytes: file.size,
        });

        await axios.put(presign.uploadUrl, file, {
          headers: {
            'Content-Type': file.type,
          },
        });

        const imageDimensions = await readImageDimensions(file);

        return {
          storageKey: presign.storageKey,
          fileName: file.name,
          mimeType: file.type,
          sizeBytes: file.size,
          ...imageDimensions,
        };
      }),
    );

    return attachments;
  }

  async function handleCreateTicket(values: {
    subject: string;
    priority: TicketPriority;
    message: string;
    files: File[];
  }) {
    if (!canCreateTicket) {
      toast.error(text.onlyAdminsCanCreate);
      return false;
    }

    const subject = values.subject.trim();
    const message = values.message.trim();

    if (!subject) {
      toast.error(text.ticketSubjectRequired);
      return false;
    }

    if (!hasMessageOrAttachment(message, values.files)) {
      toast.error(text.addMessageOrImage);
      return false;
    }

    try {
      const attachments = await uploadAttachments(values.files);
      const payload: CreateTicketPayload = {
        subject,
        priority: values.priority,
        attachments,
      };

      if (message) {
        payload.message = message;
      }

      const createdTicket = await createTicket(payload);
      setSelectedTicketId(createdTicket.id);
      toast.success(text.ticketCreatedSuccess);
      return true;
    } catch (error) {
      handleTicketErrorToast(error, text.createTicketFailed);
      return false;
    }
  }

  async function handleReplyTicket(values: { message: string; files: File[] }) {
    if (!selectedTicket) {
      toast.error(text.selectTicketToReply);
      return false;
    }

    const message = values.message.trim();

    if (!hasMessageOrAttachment(message, values.files)) {
      toast.error(text.addMessageOrImage);
      return false;
    }

    if (isReplyBlocked) {
      if (replyBlockedMessage) {
        toast.error(replyBlockedMessage);
      }
      return false;
    }

    try {
      const attachments = await uploadAttachments(values.files);
      const payload: {
        message?: string;
        attachments: TicketAttachmentPayload[];
      } = {
        attachments,
      };

      if (message) {
        payload.message = message;
      }

      await sendTicketMessage({
        ticketId: selectedTicket.id,
        payload,
      });

      toast.success(text.messageSentSuccess);
      return true;
    } catch (error) {
      const status = getStatusFromError(error);

      if (status === 409) {
        handleTicketErrorToast(error, text.sendMessageConflict);
        return false;
      }

      handleTicketErrorToast(error, text.sendMessageFailed);
      return false;
    }
  }

  async function handleFinalizeTicket() {
    if (!selectedTicket || !isRoot) {
      return;
    }

    try {
      await finalizeTicket(selectedTicket.id);
      toast.success(text.ticketFinalizedSuccess);
    } catch (error) {
      const status = getStatusFromError(error);

      if (status === 409) {
        handleTicketErrorToast(error, text.ticketAlreadyFinalized);
        return;
      }

      handleTicketErrorToast(error, text.finalizeTicketFailed);
    }
  }

  async function handleReopenTicket() {
    if (!selectedTicket || !isRoot) {
      return;
    }

    try {
      await reopenTicket(selectedTicket.id);
      toast.success(text.ticketReopenedSuccess);
    } catch (error) {
      const status = getStatusFromError(error);

      if (status === 409) {
        handleTicketErrorToast(error, text.ticketNotFinalized);
        return;
      }

      handleTicketErrorToast(error, text.reopenTicketFailed);
    }
  }

  async function handleUpdateTicketPriority() {
    if (!selectedTicket || !isRoot) {
      return;
    }

    const currentPriority = selectedTicket.priority ?? 'low';

    if (selectedTicketPriority === currentPriority) {
      return;
    }

    try {
      await updateTicketPriority({
        ticketId: selectedTicket.id,
        priority: selectedTicketPriority,
      });
      toast.success(text.ticketPriorityUpdatedSuccess);
    } catch (error) {
      handleTicketErrorToast(error, text.ticketPriorityUpdateFailed);
    }
  }

  async function handleDownloadAttachment(attachment: TicketAttachment) {
    try {
      setDownloadingAttachmentId(attachment.id);
      const response = await TicketsService.getAttachmentDownloadUrl(
        attachment.id,
      );

      window.open(response.downloadUrl, '_blank', 'noopener,noreferrer');
    } catch (error) {
      handleTicketErrorToast(error, text.downloadLinkFailed);
    } finally {
      setDownloadingAttachmentId(null);
    }
  }

  function handleRetry() {
    refetchTickets();
    refetchSelectedTicket();
  }

  function renderAccessDenied() {
    return (
      <Card>
        <CardContent className="flex items-center gap-3 py-8 text-red-600">
          <AlertTriangle className="h-5 w-5 shrink-0" />
          <p>{text.accessDenied}</p>
        </CardContent>
      </Card>
    );
  }

  function renderMainContent() {
    if (!hasTicketAccess) {
      return renderAccessDenied();
    }

    return (
      <div className="space-y-4">
        <header className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">
              {text.title}
            </h1>
            <p className="text-sm text-gray-600">
              {isRoot ? text.descriptionRoot : text.descriptionAdmin}
            </p>
          </div>

          <Button
            type="button"
            variant="outline"
            className="gap-2"
            onClick={handleRetry}
          >
            <RefreshCw className="h-4 w-4" />
            {text.refresh}
          </Button>
        </header>

        {canCreateTicket && variant === 'default' && (
          <CreateTicketFormCard
            text={{
              openNewTicket: text.openNewTicket,
              subjectLabel: text.subjectLabel,
              subjectPlaceholder: text.subjectPlaceholder,
              priorityLabel: text.priorityLabel,
              messageLabel: text.messageLabel,
              messagePlaceholder: text.messagePlaceholder,
              imagesLabel: text.imagesLabel,
              imagesHint: text.imagesHint,
              createAttachFilesButton: text.createAttachFilesButton,
              createTicketButton: text.createTicketButton,
              priorityLabels: text.priorityLabels,
              ticketSubjectRequired: text.ticketSubjectRequired,
              addMessageOrImage: text.addMessageOrImage,
              invalidFileType: text.invalidFileType,
              maxFileSize: text.maxFileSize,
              maxImagesPerMessage: text.maxImagesPerMessage,
            }}
            isSubmitting={isCreatingTicket}
            maxAttachmentsPerMessage={MAX_ATTACHMENTS_PER_MESSAGE}
            maxImageSizeBytes={MAX_IMAGE_SIZE_BYTES}
            allowedMimeTypes={ALLOWED_MIME_TYPES}
            onSubmit={handleCreateTicket}
          />
        )}

        <div className="grid gap-4 xl:grid-cols-[360px,1fr]">
          <Card>
            <CardHeader className="space-y-4">
              <CardTitle className="text-base">
                {text.ticketListTitle}
              </CardTitle>
              {isAdminVariant && (
                <div className="space-y-3 rounded-md border bg-gray-50 p-3">
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-sm font-medium text-gray-700">
                      {text.filterTitle}
                    </p>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={clearListFilters}
                    >
                      {text.clearFiltersButton}
                    </Button>
                  </div>

                  <div className="grid gap-3">
                    <div className="space-y-1">
                      <label className="text-xs font-medium text-gray-600">
                        {text.filterPriorityLabel}
                      </label>
                      <Select
                        value={priorityFilter}
                        onValueChange={(value: PriorityFilter) =>
                          setPriorityFilter(value)
                        }
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {text.priorityFilters.map((filter) => (
                            <SelectItem key={filter.value} value={filter.value}>
                              {filter.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-1">
                      <label className="text-xs font-medium text-gray-600">
                        {text.filterSubjectLabel}
                      </label>
                      <Input
                        value={subjectFilter}
                        onChange={(event) =>
                          setSubjectFilter(event.target.value)
                        }
                        placeholder={text.filterSubjectPlaceholder}
                      />
                    </div>

                    <div className="space-y-1">
                      <label className="text-xs font-medium text-gray-600">
                        {text.filterDescriptionLabel}
                      </label>
                      <Input
                        value={descriptionFilter}
                        onChange={(event) =>
                          setDescriptionFilter(event.target.value)
                        }
                        placeholder={text.filterDescriptionPlaceholder}
                      />
                    </div>

                    {isRoot && (
                      <div className="space-y-1">
                        <label className="text-xs font-medium text-gray-600">
                          {text.filterCustomerIdLabel}
                        </label>
                        <Input
                          inputMode="numeric"
                          value={customerIdFilter}
                          onChange={(event) =>
                            setCustomerIdFilter(
                              event.target.value.replace(/[^0-9]/g, ''),
                            )
                          }
                          placeholder={text.filterCustomerIdPlaceholder}
                        />
                      </div>
                    )}
                  </div>
                </div>
              )}

              <div className="space-y-1">
                {isAdminVariant && (
                  <p className="text-xs font-medium text-gray-600">
                    {text.filterStatusLabel}
                  </p>
                )}
                <div className="flex flex-wrap gap-2">
                  {text.statusFilters.map((filter) => (
                    <Button
                      key={filter.value}
                      type="button"
                      size="sm"
                      variant={
                        statusFilter === filter.value ? 'default' : 'outline'
                      }
                      onClick={() => setStatusFilter(filter.value)}
                    >
                      {filter.label}
                    </Button>
                  ))}
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <TicketListContent
                isLoading={isTicketsLoading}
                ticketsError={ticketsError}
                tickets={tickets}
                selectedTicketId={selectedTicketId}
                isAdminVariant={isAdminVariant}
                dateLocale={dateLocale}
                text={text}
                onRetry={handleRetry}
                onSelectTicket={setSelectedTicketId}
                renderTicketOwnership={renderTicketOwnership}
              />

              <div className="flex items-center justify-between border-t pt-3">
                <p className="text-xs text-gray-500">
                  {text.pageLabel(currentPage, totalPages)}
                </p>

                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    disabled={currentPage <= 1}
                    onClick={() =>
                      setOffset((previous) => Math.max(previous - limit, 0))
                    }
                  >
                    {text.previousButton}
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    disabled={currentPage >= totalPages}
                    onClick={() => setOffset((previous) => previous + limit)}
                  >
                    {text.nextButton}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="space-y-3">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="space-y-1">
                  <CardTitle className="text-base">
                    {text.conversationTitle}
                  </CardTitle>
                  {isAdminVariant &&
                    selectedTicket &&
                    renderTicketOwnership(selectedTicket)}
                </div>

                {selectedTicket && (
                  <div className="flex items-center gap-2">
                    <Badge
                      variant="outline"
                      className={cn(
                        getPriorityClassName(selectedTicket.priority),
                      )}
                    >
                      {getPriorityLabel(
                        selectedTicket.priority,
                        text.priorityLabels,
                      )}
                    </Badge>
                    <Badge
                      variant="outline"
                      className={cn(getStatusClassName(selectedTicket.status))}
                    >
                      {getStatusLabel(selectedTicket.status, text.statusLabels)}
                    </Badge>
                    {isRoot && selectedTicket.status === 'finalized' && (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        disabled={isReopeningTicket}
                        onClick={handleReopenTicket}
                      >
                        {isReopeningTicket ? (
                          <LoaderCircle className="h-4 w-4 animate-spin" />
                        ) : (
                          text.reopenTicketButton
                        )}
                      </Button>
                    )}
                    {isRoot && selectedTicket.status !== 'finalized' && (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        disabled={isFinalizingTicket}
                        onClick={handleFinalizeTicket}
                      >
                        {isFinalizingTicket ? (
                          <LoaderCircle className="h-4 w-4 animate-spin" />
                        ) : (
                          text.finalizeTicketButton
                        )}
                      </Button>
                    )}
                  </div>
                )}
              </div>

              {selectedTicket && (
                <div className="space-y-1 text-sm text-gray-600">
                  <p className="font-medium text-gray-900">
                    #{selectedTicket.id} - {selectedTicket.subject}
                  </p>
                  <p>
                    {text.currentPriorityLabel(
                      getPriorityLabel(
                        selectedTicket.priority,
                        text.priorityLabels,
                      ),
                    )}
                  </p>
                  <p>
                    {text.initialPriorityLabel(
                      getPriorityLabel(
                        selectedTicket.initialPriority,
                        text.priorityLabels,
                      ),
                    )}
                  </p>
                  {selectedTicket.priorityUpdatedAt ? (
                    <p>
                      {text.lastPriorityUpdateLabel(
                        getTicketPriorityUpdatedByName(
                          selectedTicket,
                          text.userFallback,
                        ) ?? '-',
                        getFormattedDate(
                          selectedTicket.priorityUpdatedAt,
                          dateLocale,
                        ),
                      )}
                    </p>
                  ) : (
                    <p>{text.neverUpdatedPriorityLabel}</p>
                  )}
                  <p>
                    {text.createdAtLabel(
                      getFormattedDate(selectedTicket.createdAt, dateLocale),
                    )}
                  </p>
                  {selectedTicket.finalizedAt && (
                    <p>
                      {text.finalizedAtLabel(
                        getFormattedDate(
                          selectedTicket.finalizedAt,
                          dateLocale,
                        ),
                      )}
                    </p>
                  )}

                  {isRoot && (
                    <div className="mt-2 flex flex-wrap items-center gap-2">
                      <Select
                        value={selectedTicketPriority}
                        onValueChange={(value: TicketPriority) =>
                          setSelectedTicketPriority(value)
                        }
                        disabled={isUpdatingPriority}
                      >
                        <SelectTrigger className="w-[170px]">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="low">
                            {text.priorityLabels.low}
                          </SelectItem>
                          <SelectItem value="high">
                            {text.priorityLabels.high}
                          </SelectItem>
                          <SelectItem value="critical">
                            {text.priorityLabels.critical}
                          </SelectItem>
                        </SelectContent>
                      </Select>

                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        disabled={
                          isUpdatingPriority ||
                          selectedTicketPriority ===
                            (selectedTicket.priority ?? 'low')
                        }
                        onClick={handleUpdateTicketPriority}
                      >
                        {isUpdatingPriority ? (
                          <span className="flex items-center gap-2">
                            <LoaderCircle className="h-4 w-4 animate-spin" />
                            {text.priorityUpdatePendingButton}
                          </span>
                        ) : (
                          text.updatePriorityButton
                        )}
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </CardHeader>

            <CardContent className="space-y-4">
              <TicketMessageList
                selectedTicket={selectedTicket}
                isLoading={isSelectedTicketLoading}
                error={selectedTicketError}
                text={text}
                dateLocale={dateLocale}
                downloadingAttachmentId={downloadingAttachmentId}
                onRetry={handleRetry}
                onDownloadAttachment={handleDownloadAttachment}
                getMessageAuthorLabel={getMessageAuthorLabel}
              />

              <ReplyTicketComposer
                text={{
                  replyTicketTitle: text.replyTicketTitle,
                  replyPlaceholder: text.replyPlaceholder,
                  replyAttachFilesButton: text.replyAttachFilesButton,
                  replyImagesHint: text.replyImagesHint,
                  sendMessageButton: text.sendMessageButton,
                  addMessageOrImage: text.addMessageOrImage,
                  invalidFileType: text.invalidFileType,
                  maxFileSize: text.maxFileSize,
                  maxImagesPerMessage: text.maxImagesPerMessage,
                }}
                isSubmitting={isSendingTicketMessage}
                isDisabled={!selectedTicket || isReplyBlocked}
                blockedMessage={replyBlockedMessage}
                showBlockedMessage={Boolean(
                  replyBlockedMessage && selectedTicket,
                )}
                maxAttachmentsPerMessage={MAX_ATTACHMENTS_PER_MESSAGE}
                maxImageSizeBytes={MAX_IMAGE_SIZE_BYTES}
                allowedMimeTypes={ALLOWED_MIME_TYPES}
                onSubmit={handleReplyTicket}
              />
            </CardContent>
          </Card>
        </div>

        <SlaCard
          title={text.slaTitle}
          description={text.slaDescription}
          severityHeader={text.slaSeverityHeader}
          responseHeader={text.slaResponseHeader}
          priorityLabels={text.priorityLabels}
          responseByPriority={text.slaResponseByPriority}
        />
      </div>
    );
  }

  if (variant === 'admin') {
    return (
      <Card className="min-h-screen rounded-lg shadow">
        <CardContent className="px-4 py-6">{renderMainContent()}</CardContent>
      </Card>
    );
  }

  return <section className="py-6">{renderMainContent()}</section>;
}
